import { useState } from 'react';
import {
  useCompBenefits, useBenefitDetail, useEmployees,
  type BenefitPackage, type BenefitLevers,
} from '../../../hooks';
import { useConfirm } from '../../../components/ui/confirm';
import { useToast } from '../../../components/ui/toast';
import { Modal } from '../../../components/ui/Modal';
import { inputCls, btnPrimary, btnGhost, Field, EmptyState, TableShell } from './_shared';
import { LEVER_FIELDS, LEVER_GROUPS, leverSummary, unitHint } from './_levers';

/** สวัสดิการ — benefit packages (a bundle of comp levers) + assign to employees. */

interface EmpRow { id: string; first_name: string; last_name: string; nickname?: string | null; status: string }

export function BenefitsTab() {
  const benefits = useCompBenefits();
  const confirm = useConfirm();
  const toast = useToast();
  const rows = (benefits.data ?? []).filter((p) => p.status !== 'archived');

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<BenefitPackage | null>(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  // levers แต่ละช่องเก็บเป็น string (input) keyed by lever key
  const [leverInputs, setLeverInputs] = useState<Record<string, string>>({});
  const [detailId, setDetailId] = useState<string | null>(null);

  function setLever(key: string, v: string) { setLeverInputs((p) => ({ ...p, [key]: v })); }

  function openNew() { setEditing(null); setName(''); setDesc(''); setLeverInputs({}); setOpen(true); }
  function openEdit(p: BenefitPackage) {
    setEditing(p); setName(p.name); setDesc(p.description ?? '');
    const l = (p.levers ?? {}) as Record<string, number | undefined>;
    const next: Record<string, string> = {};
    for (const f of LEVER_FIELDS) next[f.key] = l[f.key] != null ? String(l[f.key]) : '';
    setLeverInputs(next);
    setOpen(true);
  }
  async function onDelete(p: BenefitPackage) {
    if (!(await confirm({ title: `เก็บแพ็กเกจ "${p.name}" เข้าคลัง?`, danger: true, confirmText: 'เก็บ' }))) return;
    benefits.del.mutate(p.id, {
      onSuccess: () => toast.success('เก็บแล้ว'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ'),
    });
  }
  function num(s: string) { return s ? Number(s) : undefined; }
  function onSubmit() {
    if (!name.trim()) { toast.error('กรอกชื่อแพ็กเกจ'); return; }
    const levers: BenefitLevers = {};
    for (const f of LEVER_FIELDS) {
      const v = num(leverInputs[f.key] ?? '');
      if (v != null) (levers as Record<string, number>)[f.key] = v;
    }
    benefits.save.mutate(
      { id: editing?.id, body: { name: name.trim(), description: desc.trim() || null, levers } },
      {
        onSuccess: () => { toast.success('บันทึกแล้ว'); setOpen(false); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
      },
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">ชุดสวัสดิการ/ค่าตอบแทน ที่นำไปมอบให้พนักงานหลายคนพร้อมกัน</p>
        <button onClick={openNew} className={btnPrimary}>+ เพิ่มแพ็กเกจ</button>
      </div>

      {benefits.isLoading ? (
        <p className="py-6 text-center text-sm text-gray-400">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <EmptyState text="ยังไม่มีแพ็กเกจสวัสดิการ" />
      ) : (
        <TableShell head={
          <tr>
            <th className="px-3 py-2 font-medium">ชื่อแพ็กเกจ</th>
            <th className="px-3 py-2 font-medium">สาระสำคัญ</th>
            <th className="px-3 py-2 font-medium">มอบให้ (คน)</th>
            <th className="px-3 py-2"></th>
          </tr>
        }>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-gray-100">
              <td className="px-3 py-2 font-medium text-gray-900">{p.name}</td>
              <td className="px-3 py-2 text-gray-500">{leverSummary(p.levers)}</td>
              <td className="px-3 py-2 text-gray-500">{p.assignment_count ?? 0}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button onClick={() => setDetailId(p.id)} className="text-teal-700 hover:underline">มอบ/ดูรายชื่อ</button>
                <button onClick={() => openEdit(p)} className="ml-3 text-teal-700 hover:underline">แก้ไข</button>
                <button onClick={() => void onDelete(p)} className="ml-3 text-red-600 hover:underline">เก็บ</button>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? 'แก้ไขแพ็กเกจ' : 'เพิ่มแพ็กเกจ'}>
        <div className="space-y-3">
          <Field label="ชื่อแพ็กเกจ"><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="เช่น แพ็กเกจพนักงานดีเด่น" /></Field>
          <Field label="คำอธิบาย (ไม่บังคับ)"><input className={inputCls} value={desc} onChange={(e) => setDesc(e.target.value)} /></Field>
          {LEVER_GROUPS.map((group) => (
            <div key={group}>
              <p className="mb-1 text-xs font-medium text-gray-400">{group}</p>
              <div className="grid grid-cols-2 gap-2">
                {LEVER_FIELDS.filter((f) => f.group === group).map((f) => (
                  <Field key={f.key} label={`${f.label}${unitHint(f.unit)}`}>
                    <input
                      className={inputCls}
                      type="number"
                      value={leverInputs[f.key] ?? ''}
                      onChange={(e) => setLever(f.key, e.target.value)}
                    />
                  </Field>
                ))}
              </div>
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setOpen(false)} className={btnGhost}>ยกเลิก</button>
            <button onClick={onSubmit} disabled={benefits.save.isPending} className={btnPrimary}>บันทึก</button>
          </div>
        </div>
      </Modal>

      {detailId && <AssignModal id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function AssignModal({ id, onClose }: { id: string; onClose: () => void }) {
  const benefits = useCompBenefits();
  const detail = useBenefitDetail(id);
  const employeesQ = useEmployees() as { data: EmpRow[] | undefined };
  const toast = useToast();
  const [picked, setPicked] = useState<string[]>([]);

  const emps = (employeesQ.data ?? []).filter((e) => e.status === 'active' || e.status === 'probation');
  const assignments = detail.data?.assignments ?? [];
  const assignedIds = new Set(assignments.map((a) => a.employee_id));
  const available = emps.filter((e) => !assignedIds.has(e.id));

  function toggle(eid: string) {
    setPicked((p) => (p.includes(eid) ? p.filter((x) => x !== eid) : [...p, eid]));
  }
  function doAssign() {
    if (!picked.length) { toast.error('เลือกพนักงานอย่างน้อย 1 คน'); return; }
    benefits.assign.mutate(
      { id, employee_ids: picked },
      {
        onSuccess: (r) => { toast.success(`มอบให้ ${r.assigned} คนแล้ว`); setPicked([]); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ'),
      },
    );
  }
  function remove(aid: string) {
    benefits.unassign.mutate(aid, {
      onSuccess: () => toast.success('นำออกแล้ว'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ'),
    });
  }

  return (
    <Modal open onClose={onClose} title={`มอบแพ็กเกจ: ${detail.data?.name ?? ''}`}>
      <div className="space-y-4">
        <div>
          <p className="mb-2 text-sm font-medium">พนักงานที่ได้รับแล้ว ({assignments.length})</p>
          {assignments.length === 0 ? (
            <EmptyState text="ยังไม่ได้มอบให้ใคร" />
          ) : (
            <div className="space-y-1">
              {assignments.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm">
                  <span>{a.employee_name || a.employee_id}</span>
                  <button onClick={() => remove(a.id)} className="text-red-600 hover:underline">นำออก</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="mb-2 text-sm font-medium">เพิ่มพนักงาน</p>
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2">
            {available.length === 0 ? (
              <p className="p-2 text-sm text-gray-400">ไม่มีพนักงานเพิ่มเติม</p>
            ) : (
              available.map((e) => (
                <label key={e.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50">
                  <input type="checkbox" checked={picked.includes(e.id)} onChange={() => toggle(e.id)} />
                  <span>{e.first_name} {e.last_name}{e.nickname ? ` (${e.nickname})` : ''}</span>
                </label>
              ))
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className={btnGhost}>ปิด</button>
          <button onClick={doAssign} disabled={benefits.assign.isPending || picked.length === 0} className={btnPrimary}>
            มอบให้ {picked.length || ''} คน
          </button>
        </div>
      </div>
    </Modal>
  );
}
